import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  useDirection,
  useProperty,
  useSiteContext,
  useSnowplowTracking,
  useTranslation,
} from '@eti/providers';
import { css, cx } from '@eti/styles';
import { CarrierLogo, ContentWrapper } from '@eti/components';
import { breakpoints, Column, Columns, Inline, Stack, Text, textVariants, vars } from '@etg/wings';
import { OrderState, PartOfExternalFlights } from '@eti/schema-types';
import { CaretRight } from '@phosphor-icons/react';
import { useMediaQuery } from 'usehooks-ts';
import { CO2_COMPENSATION } from '@eti/products';
import RefundStatus from '../refund-status/RefundStatus';
import { routes } from '../../../../constants/routesAndApisConstants';
import { useScrollToElementViaQueryParams } from '../../../../common/custom-hooks/scrollToElementViaQueryParams';
import Banners from '../../../../common/components/Banners';
import TitledCard from '../../../../common/components/TitledCard';
import SearchFlightsBanner from '../../../my-bookings/components/banners/SearchFlightsBanner';
import { SHOW_REFUND_STATUS_QUERY_PARAMETER } from '../../constants/general';
import { screenReadersOnlyStyles } from '../../../../utils/styleUtils';
import RyanairFlightInfoBox from '../common/RyanairFlightInfoBox';
import { isUpcoming, shouldShowRefundStatusInformation } from '../../utils';
import ClickableCard from '../../../../common/components/ClickableCard';
import { useOrderDetails } from '../../containers/OrderDetailsContext';
import { useRoutedTabs } from '../../../../widgets/tabs/components/RoutedTabs';
import AppBanner from '../common/AppBanner';
import FaqSection from '../common/FaqSection';
import type { AvailableExtraProductType } from '../../models';
import AirlineInformation from './AirlineInformation';
import Header from './header/Header';
import Payment from './payment/Payment';
import Itinerary from './itinerary/Itinerary';
import HotelsAndCars from './HotelsAndCars';
import Documents from './documents/Documents';
import CarrierVerification from './carrier-verification/CarrierVerification';
import Passengers from './passengers/Passengers';
import { getTransformedBaggagePerBound } from './baggages/utils';
import ProductsSection from './products-overview/ProductsSection';
import { getUpsellProductsPrioritised } from './utils';
import AgentInfo from './AgentInfo';
import AgencyFAQ from './AgencyFAQ';
import CancellationCertificate from './cancellation-certificate/CancellationCertificate';
import UpsellProducts from './UpsellProducts';
import UpsellProductBanner from './UpsellProductBanner';
import CO2CompensationBanner from './CO2CompensationBanner';

const contentWrapperStyles = css`
  margin-bottom: 40px;
  margin-top: 16px;

  @media (min-width: ${breakpoints._768}) {
    margin-top: 24px;
  }
`;

const caretButtonStyles = css`
  background-color: #fff;
  border-radius: ${vars.card.borderRadius};
  padding: 16px;

  &:focus-visible {
    outline-offset: -0.1875rem;
  }
`;

const carrierLogoStyles = css`
  width: 32px;
`;

const inlineStyles = css`
  flex: 1;
`;

const resetLinkStyles = css`
  color: inherit;
  text-decoration: none;
`;

const OrderDetailsOverview = () => {
  const { p } = useProperty();
  const { t } = useTranslation();
  const { isRTL } = useDirection();
  const { agencyAccount } = useSiteContext();
  const { isSnowplowTrackingEnabled, sendTrackEvent } = useSnowplowTracking();
  const {
    ancillaryAvailabilityLoading,
    availableExtraProducts,
    bounds,
    cabinBaggageTripSummary,
    checkedBaggageSummary,
    faqSectionPerSlug,
    hasRyanairLCCSupplier,
    isEligibleForCancellationCertificate,
    isUpsellAllowed,
    isWhiteLabelBrand,
    journeyType,
    orderState,
    partOfExternalFlights,
    personalItemTripSummary,
    refundStatus,
    secondBagTripSummary,
    travelers,
  } = useOrderDetails();
  const { tabHeading1Ref } = useRoutedTabs();

  const transformedBaggagePerBound = useMemo(
    () =>
      getTransformedBaggagePerBound(
        bounds,
        travelers,
        cabinBaggageTripSummary,
        checkedBaggageSummary,
        personalItemTripSummary,
        secondBagTripSummary,
        journeyType,
      ),
    [
      bounds,
      cabinBaggageTripSummary,
      checkedBaggageSummary,
      journeyType,
      personalItemTripSummary,
      secondBagTripSummary,
      travelers,
    ],
  );

  const isOrderActive = orderState !== OrderState.Canceled;
  const isOrderModified = orderState === OrderState.Canceled || orderState === OrderState.Changed;

  const faqSectionSlug = p('OrderDetails.Overview.Faq.Slug');

  const shouldShowHotelsAndCarsBanners =
    !agencyAccount &&
    isUpsellAllowed &&
    orderState !== OrderState.Changed &&
    isOrderActive &&
    !isWhiteLabelBrand &&
    isUpcoming(bounds);

  const shouldShowTravelInformationButton = p('OrderDetails.TravelInformation.Enabled');
  const leftColumnComponentCodes = p('OrderDetails.Overview.Components.Order.LeftColumn');
  const rightColumnComponentCodes = p('OrderDetails.Overview.Components.Order.RightColumn');
  const getIndex = (id: string, componentIds: string[]) => {
    return componentIds.indexOf(id) + 1;
  };
  const shouldShowAppBanner = !agencyAccount && p('Order.DownloadApp.Enabled');

  const isMobileScreen = useMediaQuery(`(max-width: ${breakpoints._768})`);

  const shouldShowRyanairVerification =
    isOrderActive && p('OrderDetails.RyanairVerification.Enabled') && hasRyanairLCCSupplier;

  const shouldShowAgencyFAQ = agencyAccount && p('OrderDetails.Agency.FAQ.Url');

  useScrollToElementViaQueryParams(
    'refund-status-section-heading',
    SHOW_REFUND_STATUS_QUERY_PARAMETER,
  );

  const handleSnowplowOnTravelInformation = () => {
    if (isSnowplowTrackingEnabled) {
      sendTrackEvent({
        category: 'Overview',
        action: 'Click',
        label: 'TRAVEL_INFORMATION',
      });
    }
  };

  const upsellProducts =
    availableExtraProducts && availableExtraProducts.length > 0
      ? getUpsellProductsPrioritised(availableExtraProducts, p('OrderDetails.UpsellProducts.Order'))
      : null;

  const co2CompensationProduct =
    availableExtraProducts && availableExtraProducts.length > 0
      ? availableExtraProducts.find(
          (product: AvailableExtraProductType) => Number(product?.id) === CO2_COMPENSATION.id,
        )
      : null;

  const getContentByCode = (code: string) => {
    switch (code) {
      case 'ITINERARY':
        return (
          <div
            data-testid={`overview-left-itinerary-${getIndex(
              'ITINERARY',
              leftColumnComponentCodes,
            )}`}
            key={code}
          >
            <Itinerary />
          </div>
        );
      case 'UPSELL_PRODUCT_BANNER':
        return (
          isOrderActive &&
          !ancillaryAvailabilityLoading &&
          upsellProducts?.primaryUpsellProduct && (
            <div
              data-testid={`overview-left-upsell-product-banner-${getIndex(
                'UPSELL_PRODUCT_BANNER',
                leftColumnComponentCodes,
              )}`}
              key={code}
            >
              <UpsellProductBanner product={upsellProducts.primaryUpsellProduct} />
            </div>
          )
        );
      case 'BAGGAGE':
        return (
          <div
            data-testid={`overview-left-products-section-${getIndex(
              'BAGGAGE',
              leftColumnComponentCodes,
            )}`}
            key={code}
          >
            <ProductsSection baggagePerBound={transformedBaggagePerBound} />
          </div>
        );
      case 'UPSELL_PRODUCTS':
        return (
          isOrderActive && (
            <div
              data-testid={`overview-left-upsell-products-${getIndex(
                'UPSELL_PRODUCTS',
                leftColumnComponentCodes,
              )}`}
              key={code}
            >
              <UpsellProducts products={upsellProducts?.secondaryUpsellProducts} />
            </div>
          )
        );
      case 'PASSENGER':
        return (
          <div
            data-testid={`overview-right-passenger-${getIndex(
              'PASSENGER',
              rightColumnComponentCodes,
            )}`}
            key={code}
          >
            <Passengers baggagePerBound={transformedBaggagePerBound} />
          </div>
        );
      case 'AIRLINE_BOOKING_DETAILS':
        return (
          <div
            data-testid={`overview-right-airline-booking-details-${getIndex(
              'AIRLINE_BOOKING_DETAILS',
              rightColumnComponentCodes,
            )}`}
            key={code}
          >
            <AirlineInformation />
          </div>
        );
      case 'PAYMENT':
        return (
          <div
            data-testid={`overview-right-payment-${getIndex('PAYMENT', rightColumnComponentCodes)}`}
            key={code}
          >
            <Payment />
          </div>
        );
      case 'DOCUMENTS':
        return (
          <div
            data-testid={`overview-right-documents-${getIndex(
              'DOCUMENTS',
              rightColumnComponentCodes,
            )}`}
            key={code}
          >
            <Documents />
          </div>
        );
      case 'HOTEL_AND_RENTALCARS':
        return (
          shouldShowHotelsAndCarsBanners && (
            <div
              data-testid={`overview-right-hotel-and-rentalcars-${getIndex(
                'HOTEL_AND_RENTALCARS',
                rightColumnComponentCodes,
              )}`}
              key={code}
            >
              <HotelsAndCars dataTestIdPrefix="order-details-overview" />
            </div>
          )
        );
      case 'WHITE_LABEL_BANNER':
        return (
          isWhiteLabelBrand && (
            <div
              data-testid={`overview-right-branded-banners-${getIndex(
                'WHITE_LABEL_BANNER',
                rightColumnComponentCodes,
              )}`}
              key={code}
            >
              <Banners />
            </div>
          )
        );
      case 'CO2_COMPENSATION':
        return (
          isOrderActive &&
          isUpsellAllowed &&
          co2CompensationProduct && (
            <div
              data-testid={`overview-right-co2-compensation-banner-${getIndex(
                'CO2_COMPENSATION',
                rightColumnComponentCodes,
              )}`}
              key={code}
            >
              <CO2CompensationBanner product={co2CompensationProduct} />
            </div>
          )
        );
      default:
        return null;
    }
  };

  return (
    <div data-testid="order-details-overview">
      <ContentWrapper className={contentWrapperStyles}>
        <h1 className={screenReadersOnlyStyles} ref={tabHeading1Ref} tabIndex={-1}>
          {t('OrderDetails.Tab.Overview.Description')}
        </h1>
        <Stack spacing={{ [breakpoints._0]: 16, [breakpoints._768]: 24 }}>
          {shouldShowRyanairVerification && isMobileScreen && <CarrierVerification />}
          <Columns
            collapseBelow={breakpoints._768}
            spacing={{ [breakpoints._0]: 16, [breakpoints._768]: 24 }}
          >
            <Column width={{ [breakpoints._0]: '12/12', [breakpoints._768]: '7/12' }}>
              <Stack spacing={{ [breakpoints._0]: 16, [breakpoints._768]: 24 }}>
                <Header />
                {agencyAccount && <AgentInfo />}
                {isOrderModified && shouldShowRefundStatusInformation(refundStatus) && (
                  <TitledCard
                    data-testid="order-details-refund-status"
                    headingId="refund-status-section-heading"
                    title={t('OrderDetails.RefundStatus.Title')}
                  >
                    <RefundStatus />
                  </TitledCard>
                )}
                {isEligibleForCancellationCertificate && <CancellationCertificate />}
                {partOfExternalFlights !== PartOfExternalFlights.None && (
                  <TitledCard
                    data-testid="order-details-ryanair-info-box"
                    icon={
                      <CarrierLogo
                        className={carrierLogoStyles}
                        data-testid="ryanair-logo"
                        id="fr"
                        imageAlt="Ryanair"
                      />
                    }
                    title={t('OrderDetails.RyanairFlight.InfoBox.Title')}
                  >
                    <RyanairFlightInfoBox
                      isExpanded={partOfExternalFlights === PartOfExternalFlights.All}
                    />
                  </TitledCard>
                )}
                {!isOrderActive && !agencyAccount && <SearchFlightsBanner />}
                {leftColumnComponentCodes.map((code: string) => getContentByCode(code))}
                {!isMobileScreen && faqSectionPerSlug?.[faqSectionSlug] && (
                  <FaqSection
                    section={faqSectionPerSlug[faqSectionSlug]}
                    shouldRedirectToContactUs
                  />
                )}
              </Stack>
            </Column>
            <Column width={{ [breakpoints._0]: '12/12', [breakpoints._768]: '5/12' }}>
              <Stack spacing={{ [breakpoints._0]: 16, [breakpoints._768]: 24 }}>
                {shouldShowAgencyFAQ && <AgencyFAQ />}
                {shouldShowRyanairVerification && !isMobileScreen && <CarrierVerification />}
                {shouldShowTravelInformationButton && (
                  <ClickableCard
                    as={Link}
                    className={cx(caretButtonStyles, resetLinkStyles)}
                    data-testid="travel-information-button"
                    onCardSelection={() => {
                      handleSnowplowOnTravelInformation();
                    }}
                    to={routes.ORDER_DETAILS_TRAVEL_INFORMATION}
                  >
                    <Inline alignY="center" noWrap>
                      <Text className={inlineStyles} variant={textVariants._18_EMPHASIZED}>
                        {t('OrderDetails.Tab.TravelInformation.Description')}
                      </Text>
                      <CaretRight aria-hidden="true" color="#262626" mirrored={isRTL} size={24} />
                    </Inline>
                  </ClickableCard>
                )}
                {shouldShowAppBanner && <AppBanner />}
                {rightColumnComponentCodes.map((code: string) => getContentByCode(code))}
                {isMobileScreen && faqSectionPerSlug?.[faqSectionSlug] && (
                  <FaqSection
                    section={faqSectionPerSlug[faqSectionSlug]}
                    shouldRedirectToContactUs
                  />
                )}
              </Stack>
            </Column>
          </Columns>
        </Stack>
      </ContentWrapper>
    </div>
  );
};

export default OrderDetailsOverview;
